
import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { withNodeOptimization } from '../hoc/withNodeOptimization'

export type DrawingShape = 'square' | 'circle' | 'slash' | 'arrow' | 'pencil'
export type BrushSize = 'small' | 'medium' | 'large'
export type ControlNetMode = 'scribbles' | 'lines'
export type ViewMode = 'original' | 'processed'

export interface ControlNetNodeProps {
  /** Node name */
  name?: string
  /** Node ID */
  id?: string
  /** Whether node is selected */
  selected?: boolean
  /** ControlNet mode: scribbles (drawing) or lines (image processing) */
  mode?: ControlNetMode
  /** Active drawing shape/tool */
  activeShape?: DrawingShape
  /** Active brush size */
  brushSize?: BrushSize
  /** Stroke color for drawing */
  strokeColor?: string
  /** Input image source (for lines mode) */
  imageSrc?: string | null
  /** Processed image source (for lines mode) */
  processedImageSrc?: string | null
  /** Active view mode (for lines mode) */
  viewMode?: ViewMode
  /** Whether lines have been generated (for lines mode) */
  hasGeneratedLines?: boolean
  /** Whether undo is available */
  canUndo?: boolean
  /** Whether redo is available */
  canRedo?: boolean
  /** Callback when settings panel is opened */
  onSettingsClick?: () => void
  /** Callback when image input handle is clicked */
  onImageInputClick?: () => void
  /** Callback when output handle is clicked */
  onOutputHandleClick?: () => void
  /** Callback when file is uploaded */
  onFileUpload?: (file: File) => void
  /** Callback when view mode changes */
  onViewModeChange?: (mode: ViewMode) => void
  /** Callback when generate lines is clicked */
  onGenerateLines?: () => void
  /** Callback when shape changes */
  onShapeChange?: (shape: DrawingShape) => void
  /** Callback when brush size changes */
  onBrushSizeChange?: (size: BrushSize) => void
  /** Callback when stroke color changes */
  onStrokeColorChange?: (color: string) => void
  /** Callback when undo is clicked */
  onUndo?: () => void
  /** Callback when redo is clicked */
  onRedo?: () => void
  /** Additional CSS classes */
  className?: string
}

function ControlNetNodeComponent({
  name = 'ControlNet node 1',
  id,
  selected = false,
  mode = 'scribbles',
  activeShape = 'pencil',
  brushSize = 'medium',
  strokeColor = '#3b82f6',
  imageSrc = null,
  processedImageSrc = null,
  viewMode = 'processed',
  hasGeneratedLines = false,
  canUndo = true,
  canRedo = false,
  onSettingsClick,
  onImageInputClick,
  onOutputHandleClick,
  onFileUpload,
  onViewModeChange,
  onGenerateLines,
  onShapeChange,
  onBrushSizeChange,
  onStrokeColorChange,
  onUndo,
  onRedo,
  className,
}: ControlNetNodeProps) {
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null)
  const [hasDrawing, setHasDrawing] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null)

  const getBrushWidth = useCallback(() => {
    switch (brushSize) {
      case 'small':
        return 2
      case 'medium':
        return 4
      case 'large':
        return 7
      default:
        return 4
    }
  }, [brushSize])

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setIsDrawing(true)
    setStartPoint({ x, y })
    setHasDrawing(true)

    if (activeShape === 'pencil') {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.beginPath()
        ctx.moveTo(x, y)
      }
    }
  }, [activeShape])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return

    const canvas = canvasRef.current
    const previewCanvas = drawingCanvasRef.current
    if (!canvas || !previewCanvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (activeShape === 'pencil') {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.lineTo(x, y)
        ctx.strokeStyle = strokeColor
        ctx.lineWidth = getBrushWidth()
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.stroke()
      }
    } else if (startPoint) {
      // Preview shape while dragging
      const ctx = previewCanvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)
        ctx.strokeStyle = strokeColor
        ctx.lineWidth = getBrushWidth()
        ctx.lineCap = 'round'

        switch (activeShape) {
          case 'square':
            ctx.strokeRect(startPoint.x, startPoint.y, x - startPoint.x, y - startPoint.y)
            break
          case 'circle': {
            const radius = Math.sqrt(Math.pow(x - startPoint.x, 2) + Math.pow(y - startPoint.y, 2))
            ctx.beginPath()
            ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI)
            ctx.stroke()
            break
          }
          case 'slash':
          case 'arrow':
            ctx.beginPath()
            ctx.moveTo(startPoint.x, startPoint.y)
            ctx.lineTo(x, y)
            ctx.stroke()

            if (activeShape === 'arrow') {
              // Draw arrowhead
              const angle = Math.atan2(y - startPoint.y, x - startPoint.x)
              const headLength = 10
              ctx.beginPath()
              ctx.moveTo(x, y)
              ctx.lineTo(
                x - headLength * Math.cos(angle - Math.PI / 6),
                y - headLength * Math.sin(angle - Math.PI / 6)
              )
              ctx.moveTo(x, y)
              ctx.lineTo(
                x - headLength * Math.cos(angle + Math.PI / 6),
                y - headLength * Math.sin(angle + Math.PI / 6)
              )
              ctx.stroke()
            }
            break
        }
      }
    }
  }, [isDrawing, activeShape, startPoint, strokeColor, getBrushWidth])

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint) return

    const canvas = canvasRef.current
    const previewCanvas = drawingCanvasRef.current
    if (!canvas || !previewCanvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (activeShape !== 'pencil') {
      // Commit the shape to the main canvas
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.strokeStyle = strokeColor
        ctx.lineWidth = getBrushWidth()
        ctx.lineCap = 'round'

        switch (activeShape) {
          case 'square':
            ctx.strokeRect(startPoint.x, startPoint.y, x - startPoint.x, y - startPoint.y)
            break
          case 'circle': {
            const radius = Math.sqrt(Math.pow(x - startPoint.x, 2) + Math.pow(y - startPoint.y, 2))
            ctx.beginPath()
            ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI)
            ctx.stroke()
            break
          }
          case 'slash':
          case 'arrow':
            ctx.beginPath()
            ctx.moveTo(startPoint.x, startPoint.y)
            ctx.lineTo(x, y)
            ctx.stroke()

            if (activeShape === 'arrow') {
              const angle = Math.atan2(y - startPoint.y, x - startPoint.x)
              const headLength = 10
              ctx.beginPath()
              ctx.moveTo(x, y)
              ctx.lineTo(
                x - headLength * Math.cos(angle - Math.PI / 6),
                y - headLength * Math.sin(angle - Math.PI / 6)
              )
              ctx.moveTo(x, y)
              ctx.lineTo(
                x - headLength * Math.cos(angle + Math.PI / 6),
                y - headLength * Math.sin(angle + Math.PI / 6)
              )
              ctx.stroke()
            }
            break
        }
      }

      // Clear preview canvas
      const previewCtx = previewCanvas.getContext('2d')
      if (previewCtx) {
        previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)
      }
    }

    setIsDrawing(false)
    setStartPoint(null)
  }, [isDrawing, activeShape, startPoint, strokeColor, getBrushWidth])

  const handleMouseLeave = useCallback(() => {
    setIsDrawing(false)
    setStartPoint(null)

    // Clear preview canvas
    const previewCanvas = drawingCanvasRef.current
    if (previewCanvas) {
      const ctx = previewCanvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)
      }
    }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const previewCanvas = drawingCanvasRef.current
    if (canvas && previewCanvas) {
      canvas.width = 500
      canvas.height = 259
      previewCanvas.width = 500
      previewCanvas.height = 259

      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    }
  }, [])

  const getCursorStyle = () => {
    switch (activeShape) {
      case 'pencil':
        return 'cursor-crosshair'
      case 'square':
      case 'circle':
      case 'slash':
      case 'arrow':
        return 'cursor-crosshair'
      default:
        return 'cursor-default'
    }
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-2 relative w-[500px]',
        selected && 'ring-2 ring-primary ring-offset-2',
        className
      )}
    >
      {/* Node Title */}
      <div className="flex items-center gap-2 h-5">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Icon name="cpu" className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium truncate overflow-ellipsis">{name}</span>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-4 w-4 p-0 shrink-0"
          onClick={onSettingsClick}
        >
          <Icon name="panel-left" className="h-4 w-4" />
        </Button>
      </div>

      {/* Content - Mode-dependent rendering */}
      {mode === 'scribbles' ? (
        <>
          {/* Drawing Canvas */}
          <div className="bg-background border border-border rounded-[14px] h-[259px] relative">
            <div className="absolute inset-0 rounded-[14px] overflow-hidden">
              {/* Main drawing canvas */}
              <canvas
                ref={canvasRef}
                className={cn('absolute inset-0 w-full h-full', getCursorStyle())}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              />
              {/* Preview canvas for shapes */}
              <canvas
                ref={drawingCanvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
              />

              {/* Empty State */}
              {!hasDrawing && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 p-2 rounded-lg mb-0 pointer-events-none"
                  >
                    <Icon name="pencil" className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <p className="text-sm text-muted-foreground leading-5 whitespace-pre">
                    Start drawing here
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Output Handle */}
          <div className="absolute box-border flex gap-2 items-center p-2 right-[-12px] top-[calc(50%+13.5px)] -translate-y-1/2">
            <button className="relative w-2 h-2" onClick={onOutputHandleClick}>
              <div className="absolute inset-[-25%] bg-foreground border-2 border-border rounded-full" />
            </button>
          </div>

          {/* Action Toolbar */}
          <div className="absolute flex gap-1 h-9 items-center left-1/2 -translate-x-1/2 top-[303px]">
        {/* Undo/Redo */}
        <div className="bg-background border border-border rounded-[10px] flex gap-1.5 items-center p-1 h-full">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 p-1 rounded-md"
            onClick={onUndo}
            disabled={!canUndo}
          >
            <Icon name="undo" className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 p-1 rounded-md"
            onClick={onRedo}
            disabled={!canRedo}
          >
            <Icon name="redo" className="h-5 w-5 text-muted-foreground" />
          </Button>
        </div>

        {/* Shape Selection */}
        <div className="bg-background border border-border rounded-[10px] flex gap-1.5 items-center p-1 h-full">
          <Button
            size="icon"
            variant={activeShape === 'square' ? 'default' : 'ghost'}
            className={cn(
              'h-7 w-7 p-1 rounded-md',
              activeShape === 'square' && 'bg-transparent hover:bg-accent'
            )}
            onClick={() => onShapeChange?.('square')}
          >
            <Icon name="square" className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant={activeShape === 'circle' ? 'default' : 'ghost'}
            className={cn(
              'h-7 w-7 p-1 rounded-md',
              activeShape === 'circle' && 'bg-transparent hover:bg-accent'
            )}
            onClick={() => onShapeChange?.('circle')}
          >
            <Icon name="circle" className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant={activeShape === 'slash' ? 'default' : 'ghost'}
            className={cn(
              'h-7 w-7 p-1 rounded-md',
              activeShape === 'slash' && 'bg-transparent hover:bg-accent'
            )}
            onClick={() => onShapeChange?.('slash')}
          >
            <Icon name="slash" className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant={activeShape === 'arrow' ? 'default' : 'ghost'}
            className={cn(
              'h-7 w-7 p-1 rounded-md',
              activeShape === 'arrow' && 'bg-transparent hover:bg-accent'
            )}
            onClick={() => onShapeChange?.('arrow')}
          >
            <Icon name="move-up-right" className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant={activeShape === 'pencil' ? 'default' : 'ghost'}
            className={cn(
              'h-7 w-7 p-1 rounded-md',
              activeShape === 'pencil' && 'bg-foreground'
            )}
            onClick={() => onShapeChange?.('pencil')}
          >
            <Icon name="pencil" className={cn('h-5 w-5', activeShape === 'pencil' && 'text-background')} />
          </Button>
        </div>

        {/* Brush Size Selection */}
        <div className="bg-background border border-border rounded-[10px] flex gap-1.5 items-center p-1 h-full">
          <button
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-md',
              brushSize === 'small' && 'bg-transparent'
            )}
            onClick={() => onBrushSizeChange?.('small')}
          >
            <div className={cn('w-1 h-1 rounded-full', brushSize === 'small' ? 'bg-foreground' : 'bg-foreground')} />
          </button>
          <button
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-md',
              brushSize === 'medium' && 'bg-foreground'
            )}
            onClick={() => onBrushSizeChange?.('medium')}
          >
            <div className={cn('w-2 h-2 rounded-full', brushSize === 'medium' ? 'bg-background' : 'bg-foreground')} />
          </button>
          <button
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-md',
              brushSize === 'large' && 'bg-transparent'
            )}
            onClick={() => onBrushSizeChange?.('large')}
          >
            <div className={cn('w-3.5 h-3.5 rounded-full', brushSize === 'large' ? 'bg-foreground' : 'bg-foreground')} />
          </button>
        </div>

        {/* Color Picker */}
        <div className="bg-background border border-border rounded-[10px] flex gap-1.5 items-center p-1 h-full">
          <button
            className="flex items-center w-7 h-7 rounded-md p-1"
            onClick={() => onStrokeColorChange?.(strokeColor)}
          >
            <div
              className="w-full h-full rounded-full"
              style={{ backgroundColor: strokeColor }}
            />
          </button>
        </div>
          </div>
        </>
      ) : (
        <>
          {/* Lines Mode - Image Processing */}
          <div className="bg-background border border-border rounded-[14px] p-3 flex flex-col gap-3">
            {/* Image Display or Upload Area */}
            {!imageSrc && !processedImageSrc ? (
              /* Empty State - Upload Area */
              <div className="flex-1 min-h-0 bg-background border-2 border-dashed border-border rounded-[10px] flex flex-col gap-4 items-center justify-center">
                <div className="flex flex-col gap-2 items-center">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 p-2 rounded-lg pointer-events-none"
                  >
                    <Icon name="upload" className="h-4 w-4" />
                  </Button>
                  <p className="text-sm font-normal leading-5 text-foreground whitespace-pre">
                    Upload the image
                  </p>
                  <p className="text-sm font-normal leading-5 text-muted-foreground whitespace-pre">
                    PNG, JPEG. Up to 5mb
                  </p>
                </div>
              </div>
            ) : !hasGeneratedLines ? (
              /* Not Generated State - Show original image with Generate button */
              <>
                <div className="border border-border h-[259px] relative rounded-[14px] overflow-hidden">
                  <img
                    src={imageSrc || ''}
                    alt="Original"
                    className="absolute inset-0 w-full h-full object-cover rounded-[14px]"
                  />
                </div>
                <div className="flex items-end justify-end">
                  <Button
                    className="h-9 px-4 py-2 rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                    onClick={onGenerateLines}
                  >
                    <Icon name="sparkles" className="h-4 w-4" />
                    <span className="font-medium text-sm leading-5">Generate lines</span>
                  </Button>
                </div>
              </>
            ) : (
              /* Image Display with Tabs */
              <>
                <div className="border border-border h-[259px] relative rounded-[14px] overflow-hidden">
                  {viewMode === 'original' && imageSrc ? (
                    <img
                      src={imageSrc}
                      alt="Original"
                      className="absolute inset-0 w-full h-full object-cover rounded-[14px]"
                    />
                  ) : viewMode === 'processed' && processedImageSrc ? (
                    <img
                      src={processedImageSrc}
                      alt="Processed lines"
                      className="absolute inset-0 w-full h-full object-cover rounded-[14px]"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-background" />
                  )}
                </div>

                {/* Tabs */}
                <div className="bg-muted cursor-pointer flex h-9 items-center p-[3px] rounded-[10px]">
                  <button
                    className={cn(
                      'flex-1 flex gap-2 items-center justify-center px-2 py-1 rounded-lg overflow-visible',
                      viewMode === 'original' && 'bg-background'
                    )}
                    onClick={() => onViewModeChange?.('original')}
                  >
                    <p className="font-medium text-sm text-center text-foreground whitespace-pre">
                      Original
                    </p>
                  </button>
                  <button
                    className={cn(
                      'flex-1 flex gap-2 h-full items-center justify-center px-2 py-1 rounded-lg overflow-visible',
                      viewMode === 'processed' && 'bg-background'
                    )}
                    onClick={() => onViewModeChange?.('processed')}
                  >
                    <p className="font-medium text-sm text-center text-foreground whitespace-pre">
                      Lines
                    </p>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Output Handle */}
          <div className="absolute box-border flex gap-2 items-center p-2 right-[-12px] top-[calc(50%+13.5px)] -translate-y-1/2">
            <button className="relative w-2 h-2" onClick={onOutputHandleClick}>
              <div className="absolute inset-[-25%] bg-foreground border-2 border-border rounded-full" />
            </button>
          </div>

          {/* Image Input Handle */}
          <Button
            size="icon"
            variant="outline"
            className="absolute left-[-36px] top-[28px] w-8 h-8 rounded-full p-0"
            onClick={onImageInputClick}
          >
            <Icon name="image" className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  )
}

export const ControlNetNode = withNodeOptimization(ControlNetNodeComponent, {
  compareProps: ['id', 'selected', 'state', 'mediaSrc'],
})
